/**
 * Admin sessiyasi (A1).
 *
 * MUHIM: kalit `grom_admin_v2` — eski vanilla-JS panel `grom_admin_token`
 * ishlatadi. Ataylab ALOHIDA: /v2 dagi xato eski, foydalanilayotgan panelning
 * sessiyasini uchira olmaydi. Narxi — bir marta qayta kirish.
 *
 * TZ A1 xavfsizlik talablari:
 *   · sessiya 8 soat (JWT `exp` — server hal qiladi)
 *   · faolliksiz 30 daqiqada qulflanadi (klient tomonida, quyida)
 */

const TOKEN_KEY = 'grom_admin_v2'
const IDENTITY_KEY = 'grom_admin_v2_identity'

/** Faolliksiz qulflash oynasi — TZ A1. */
export const IDLE_LOCK_MS = 30 * 60 * 1000

export interface AdminIdentity {
  username: string
  /** Serverdagi `admins.role`. TZ §1.1 da 16 rol; jonli baza hozir kamrog'ini biladi. */
  role: string
  /** Bir nechta rol bo'lsa — A1.07 «rol tanlash». Hozir server bitta qaytaradi. */
  roles?: string[]
  /** Server (`GET /admin/me`) bergan atomik huquqlar. Bo'lsa — klient rol→huquq
   *  ko'prigidan USTUN turadi (server = manba). Bo'lmasa — ko'prik ishlaydi. */
  permissions?: string[]
}

interface JwtClaims {
  exp?: number
  aud?: string
  sub?: string
}

export function decodeJwt(token: string): JwtClaims | null {
  const part = token.split('.')[1]
  if (!part) return null
  try {
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/')
    const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4))
    const json = atob(base64 + pad)
    // JWT payload UTF-8 bo'lishi mumkin (kirill username) — atob latin1 beradi.
    const bytes = Uint8Array.from(json, (c) => c.charCodeAt(0))
    return JSON.parse(new TextDecoder().decode(bytes)) as JwtClaims
  } catch {
    return null
  }
}

export function getToken(): string | null {
  try {
    const t = localStorage.getItem(TOKEN_KEY)
    if (!t) return null
    // Muddati o'tgan tokenni serverga umuman yubormaymiz.
    const claims = decodeJwt(t)
    if (claims?.exp && claims.exp * 1000 <= Date.now()) {
      clearSession()
      return null
    }
    return t
  } catch {
    return null
  }
}

export function getIdentity(): AdminIdentity | null {
  try {
    const raw = localStorage.getItem(IDENTITY_KEY)
    return raw ? (JSON.parse(raw) as AdminIdentity) : null
  } catch {
    return null
  }
}

export function setSession(token: string, identity: AdminIdentity): void {
  localStorage.setItem(TOKEN_KEY, token)
  localStorage.setItem(IDENTITY_KEY, JSON.stringify(identity))
  touchActivity()
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(IDENTITY_KEY)
  localStorage.removeItem(ACTIVITY_KEY)
}

/** Token muddati tugashiga qolgan vaqt (ms). Token yo'q bo'lsa 0. */
export function sessionRemainingMs(): number {
  const t = getToken()
  if (!t) return 0
  const claims = decodeJwt(t)
  if (!claims?.exp) return 0
  return Math.max(0, claims.exp * 1000 - Date.now())
}

/* ── Faollik kuzatuvi ─────────────────────────────────────────── */

const ACTIVITY_KEY = 'grom_admin_v2_seen'

export function touchActivity(): void {
  try {
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()))
  } catch {
    /* private rejimda localStorage yozib bo'lmaydi — qulflash o'chadi */
  }
}

export function lastActivity(): number {
  const raw = localStorage.getItem(ACTIVITY_KEY)
  const n = raw ? Number(raw) : NaN
  return Number.isFinite(n) ? n : Date.now()
}

export function isIdleLocked(): boolean {
  return Date.now() - lastActivity() > IDLE_LOCK_MS
}

/* ── A1.08 Onboarding (bir marta, har username uchun) ────────────
   Server-da "onboarding ko'rdi" bayrog'i yo'q — yadroga tegmaymiz.
   Klient-saglanadi: username'lar ro'yxati. Bu SOXTA server-holat emas —
   sof UI-qadam (birinchi kirishda tanishtirish), shuning uchun klient
   manba bo'lishi halol. */

const ONBOARD_KEY = 'grom_admin_v2_onboarded'

function onboardedList(): string[] {
  try {
    const raw = localStorage.getItem(ONBOARD_KEY)
    const arr = raw ? JSON.parse(raw) : []
    return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : []
  } catch {
    return []
  }
}

export function isOnboarded(username: string): boolean {
  return onboardedList().includes(username)
}

export function markOnboarded(username: string): void {
  try {
    const list = onboardedList()
    if (!list.includes(username)) list.push(username)
    localStorage.setItem(ONBOARD_KEY, JSON.stringify(list))
  } catch {
    /* localStorage yopiq — onboarding har kirishda ko'rinadi, xavfli emas */
  }
}
